/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type Rest, ExtendService, HttpService } from '@dm/core';
import { OtaPollingPeriod, PoolingExceptionTime } from 'app/feature/settings/basic-infomation/upgrade/upgrade';
import { type Subscriber, Observable } from 'rxjs';

/**
 *  异常轮询服务
 */
@Injectable({
    providedIn: 'root',
})
export class ExceptionPollingService {
    constructor(
        private httpService: HttpService,
        private extendService: ExtendService,
    ) {}

    /**
     * 执行轮询。允许轮询连续三次有报错，不展示报错信息，第四次接口依然报错则停止轮询并展示错误信息。用后务必调用unsubscribe取消订阅！
     *
     * @param poolingPeriod 轮询间隔，单位毫秒，默认10秒
     */
    run(rests: Rest[], poolingPeriod = OtaPollingPeriod.Default): Observable<any> {
        // 报错次数
        let exceptionTime = 0;
        let timmer: NodeJS.Timeout;
        let finish = false;
        return Observable.create((observer: Subscriber<void>) => {
            const doRequest = (allowDoException = false): void => {
                if (finish) {
                    return;
                }
                const jobs$: Array<Observable<any>> = [];
                const successFn = (response: any): void => {
                    // 成功后计数清零
                    exceptionTime = 0;
                    observer.next(response);
                    timmer = setTimeout(doRequest, poolingPeriod);
                };

                const errorFn = (error: any): void => {
                    // 如果已允许报错，结束轮询
                    if (allowDoException) {
                        observer.error(error);
                        observer.complete();
                        return;
                    }

                    timmer = setTimeout(() => {
                        // 连续三次都报错后允许展示错误信息
                        doRequest(++exceptionTime >= PoolingExceptionTime.Default);
                    }, poolingPeriod);
                };

                rests.forEach((rest) => {
                    jobs$.push(this.httpService.get(rest, { doException: allowDoException }));
                });

                // 等待所有请求完成再完成，确保多个接口报错信息浮窗都能弹出
                this.extendService.forkAll(jobs$, true)
                    .subscribe(successFn, errorFn);
            };

            doRequest();

            // 取消订阅触发
            return () => {
                finish = true;
                clearTimeout(timmer);
            };
        });
    }
}
